/* istanbul ignore file */
import { useEffect, useState } from 'react';
import { useOpportunityId } from 'domain/shared/opportunity';
import useOpportunityService, {
  CONTRACT,
  SIGNING,
  UPDATE_OPPORTUNITY,
} from 'domain/sales/opportunity/opportunityService';
import { useShowContext } from 'framework/show';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { useForm, DEFAULT_ERROR_MESSAGE, isEmptyFormErrors, FormProvider } from 'framework/form';
import { usePresaleOutputService, presaleOutputValidation } from 'domain/shared/presale';
import PresaleOutputInputForm from 'domain/sales/opportunity/shared/OpportunityPipeline/button/MarkStateAsCompletedButton/PresaleOutputInputForm';
import { ButtonWithConfirmation } from 'framework/ui/ui-builder';

const MarkAsCurrentStateButton = ({ activeStep }: { activeStep: string }) => {
  const id = useOpportunityId();
  const { update } = useOpportunityService() as any;
  const { getLastPresaleOutput, updateOutput } = usePresaleOutputService();
  const { data, setData = () => {} } = useShowContext();
  const { addMessage } = useMessageService();
  const [isConfirmDisabledForOutput, setIsConfirmDisabledForOutput] = useState(false);

  const isPresaleOutputChangeVisible = activeStep === CONTRACT || activeStep === SIGNING;

  const contextValue = useForm({
    submitFormFunc: () => Promise.resolve({}),
    getValidationSchemaFunc: () => Promise.resolve(presaleOutputValidation),
  });

  useEffect(() => {
    (async () => {
      if (isPresaleOutputChangeVisible) {
        const lastOutput = await getLastPresaleOutput(id);
        const isContractRenewal = data.getValueBySource('isContractRenewal');
        const initialOutputData = lastOutput ? { ...lastOutput, isContractRenewal } : lastOutput;
        contextValue.setFormData(initialOutputData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  useEffect(() => {
    if (isPresaleOutputChangeVisible && contextValue.data?.id) {
      const outputFormData = contextValue.data;
      const outputFormErrors = {
        ...contextValue.validateForm(),
        isValidExpectedProjectStart: !outputFormData.isValidExpectedProjectStart ? ['Please, confirm!'] : [],
        isValidProjectType: !outputFormData.isValidProjectType ? ['Please, confirm!'] : [],
        isValidRequiredResources: !outputFormData.isValidRequiredResources ? ['Please, confirm!'] : [],
      };
      setIsConfirmDisabledForOutput(!isEmptyFormErrors(outputFormErrors));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPresaleOutputChangeVisible, contextValue.data]);

  const handleMarkAsCurrentStep = async () => {
    if (contextValue.data?.id) {
      const outputFormData = contextValue.data;
      try {
        await updateOutput(id, outputFormData.id, outputFormData);
      } catch (e) {
        addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
        return;
      }
    }
    try {
      const response = await update(id, { ...data.getData(), stateStep: activeStep });
      setData(response);
    } catch (e) {
      const result = await (e as any).json();
      addMessage(new ErrorMessage(result?.errors?.stateStep || DEFAULT_ERROR_MESSAGE));
    }
  };

  const getModalChildren = () => {
    return isPresaleOutputChangeVisible && contextValue.data?.id ? (
      <FormProvider value={contextValue}>
        <PresaleOutputInputForm />
      </FormProvider>
    ) : (
      <></>
    );
  };

  const getModalAdditionalMessage = () => {
    return isPresaleOutputChangeVisible && contextValue.data?.id
      ? 'Please, verify that Team structure, Project Type and Expected Project start provided in this form are actual. If not, please, change.'
      : '';
  };

  return (
    <ButtonWithConfirmation
      displayMessage={`Do you really want to mark previous state as completed? ${getModalAdditionalMessage()}`}
      submitMethod={handleMarkAsCurrentStep}
      className="step-button"
      permissionToCheck={UPDATE_OPPORTUNITY}
      modalChildren={getModalChildren()}
      modalSize={isPresaleOutputChangeVisible && contextValue.data?.id ? 'lg' : 'sm'}
      confirmBtnProps={{
        disabled: isConfirmDisabledForOutput,
      }}
    >
      Mark as current state
    </ButtonWithConfirmation>
  );
};

export default MarkAsCurrentStateButton;
